import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SOURCE_LABEL } from "./opportunityConstants";
import type { OpportunityInsert, OpportunitySource } from "./opportunityTypes";

const SOURCES: OpportunitySource[] = [
  "Convocatoria",
  "InnovationCall",
  "DirectAward",
  "PartnerLead",
  "SECOP",
  "Manual",
];

interface CreateOpportunityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  onCreate: (payload: OpportunityInsert) => Promise<void> | void;
}

export function CreateOpportunityDialog({
  open,
  onOpenChange,
  companyId,
  onCreate,
}: CreateOpportunityDialogProps) {
  const [title, setTitle] = useState("");
  const [entity, setEntity] = useState("");
  const [source, setSource] = useState<OpportunitySource>("Convocatoria");
  const [deadline, setDeadline] = useState("");
  const [budget, setBudget] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle("");
    setEntity("");
    setSource("Convocatoria");
    setDeadline("");
    setBudget("");
    setUrl("");
    setDescription("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSaving(true);
    try {
      await onCreate({
        company_id: companyId,
        title: title.trim(),
        entity: entity.trim() || null,
        source,
        status: "Detected",
        deadline: deadline || null,
        budget_cop: budget ? Number(budget) : null,
        url: url.trim() || null,
        description: description.trim() || null,
      });
      reset();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add opportunity</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="new-title">Title</Label>
            <Input
              id="new-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="FutuMed 2027 — Experimentación Tecnológica"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-entity">Entity</Label>
            <Input
              id="new-entity"
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              placeholder="Ruta N"
            />
          </div>

          <div className="space-y-2">
            <Label>Source</Label>
            <Select value={source} onValueChange={(v) => setSource(v as OpportunitySource)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SOURCE_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-deadline">Deadline</Label>
            <Input
              id="new-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-budget">Budget (COP)</Label>
            <Input
              id="new-budget"
              type="number"
              inputMode="numeric"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="new-url">URL</Label>
            <Input
              id="new-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="new-desc">Description</Label>
            <Textarea
              id="new-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !title.trim()}>
              {saving ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
